import {
  v2GroupOf,
  orderBySequence,
  supersededV1Groups,
  isV2AlignmentSupersededByV1,
  pinCardFirst,
} from "./actionCardOrder";
import { BNBERRY_CARD_STATE } from "./action-cards/bnberryConstants";

// BnBerry (EO-660) recommendation card occupies slot 3 by default; if fewer cards
// precede it, it moves up accordingly. Lives here because it affects the card
// COUNT (the spliced card is a real action card), so it must be decided in the
// single place that count is derived from.
export const BNBERRY_DEFAULT_SLOT = 2;

// Descriptor `type` values. Each maps to exactly one card component in
// ActionCenter's descriptorToElement switch; the builder never imports those
// components, so the count stays a pure, JSX-free computation.
export const CARD_TYPE = {
  CAMPAIGN: "campaign",
  PROMO_EXPIRY: "promo-expiry",
  CORRIDOR_REQUEST: "corridor-request",
  PROMO: "promo",
  CORRIDOR: "corridor",
  ARI: "ari",
  LLM: "llm",
  BNBERRY: "bnberry",
};

/**
 * The single source of truth for the Action Center card list.
 *
 * A JSX-free transcription of the card-building rules in ActionCenter.jsx: same
 * source arrays, same v1/v2 dedup (superseding), same dismiss filtering, same
 * CARD_SEQUENCE ordering, same BnBerry splice. It deliberately does NOT apply
 * pinning, fallback (Knowledge Base / Blog) cards, the carousel, or cardPosition
 * — those only reorder or decorate and never change how many action cards exist,
 * so they stay in the component.
 *
 * Because the Action Center header (`actionCount`) and the nav badge both read
 * `buildActionCards(...).length`, the two numbers are equal by construction —
 * which is the whole point: they used to be two independent counters that drifted
 * on dedup, PROMO_EXPIRY, and dismissals.
 *
 * @param {object}  args
 * @param {object}  args.data          v1 Action Center payload ({ propertyId, results })
 * @param {object}  args.v2Data        v2 (LLM) payload ({ propertyId, cards })
 * @param {Set<string>} args.dismissedKeys  keys the user has dismissed this session
 * @param {?string} args.pinnedKey     a descriptor key (EO-1893 deep link) to move to
 *   the front. Applied BEFORE the BnBerry splice so a pinned card does not push the
 *   BnBerry card off its slot 3 — matching the original ActionCenter order. Pinning
 *   only reorders, so it never changes the count; the nav badge passes none.
 * @returns {Array<{key:string,type:string,group:string,payload:object}>} ordered descriptors
 */
export const buildActionCards = ({
  data = null,
  v2Data = null,
  dismissedKeys = new Set(),
  pinnedKey = null,
} = {}) => {
  const isDismissed = (key) => dismissedKeys.has(key);

  const corridorData = data?.results?.CORRIDOR || [];
  const corridorRequestData = data?.results?.CORRIDOR_REQUEST || [];
  const ariData = data?.results?.ARI || [];
  const promoData = data?.results?.PROMO || [];
  const promoExpiry = (data?.results?.PROMO_EXPIRY || [])[0] || null;
  const bnberry = (data?.results?.BNBERRY || [])[0] || null;

  // V2 supersedes V1: when a v2 card covers the same recommendation, the v1 card
  // is a duplicate and is not filed at all. Computed once, mirroring ActionCenter.
  const supersededGroups = supersededV1Groups(v2Data?.cards);
  // V1 supersedes V2 for Alignment (EO-1679): a v1 alignment card hides the v2 one.
  const alignmentSupersededByV1 = isV2AlignmentSupersededByV1(data, v2Data);

  // Cards are filed into groups here; actionCardOrder's CARD_SEQUENCE decides the
  // order they come out in.
  const byGroup = {};
  const fileCard = (group, descriptor) => {
    if (!byGroup[group]) byGroup[group] = [];
    byGroup[group].push(descriptor);
  };

  // Bucket promo groups by type so the render order can interleave promo
  // sub-types with corridor/ARI cards.
  const promoByType = promoData.reduce((acc, g) => {
    const key = g?.type;
    if (!key) return acc;
    if (!acc[key]) acc[key] = [];
    acc[key].push(g);
    return acc;
  }, {});

  // ── v1 cards ──────────────────────────────────────────────────────────────
  (promoByType.CAMPAIGN || []).forEach((promoGroup, i) => {
    (promoGroup?.data || []).forEach((rec, r) => {
      const key = `campaign-${rec?.recommendationId ?? rec?.campaignId ?? `${i}-${r}`}`;
      if (isDismissed(key)) return;
      fileCard("CAMPAIGN", {
        key,
        type: CARD_TYPE.CAMPAIGN,
        group: "CAMPAIGN",
        payload: { rec },
      });
    });
  });

  if (promoExpiry && !isDismissed("promo-expiry")) {
    fileCard("PROMO_EXPIRY", {
      key: "promo-expiry",
      type: CARD_TYPE.PROMO_EXPIRY,
      group: "PROMO_EXPIRY",
      payload: { data: promoExpiry },
    });
  }

  corridorRequestData.forEach((request) => {
    const key = `corridor-request-${request?.recommendationId}`;
    if (isDismissed(key)) return;
    fileCard("CORRIDOR_REQUEST", {
      key,
      type: CARD_TYPE.CORRIDOR_REQUEST,
      group: "CORRIDOR_REQUEST",
      payload: { request },
    });
  });

  // The v1 promo sub-types. `type` is the API's group name; `group` is the
  // sequence entry it renders under — the two differ only for Alignment.
  const filePromoGroups = (type, group) => {
    if (supersededGroups.has(group)) return;
    (promoByType[type] || []).forEach((promoGroup, i) => {
      const key = i > 0 ? `promo-${type}-${i}` : `promo-${type}`;
      if (isDismissed(key)) return;
      fileCard(group, {
        key,
        type: CARD_TYPE.PROMO,
        group,
        payload: { promoGroup },
      });
    });
  };

  filePromoGroups("ALIGNMENT_INTELLIGENCE", "ALIGNMENT");
  filePromoGroups("LOS", "LOS");
  filePromoGroups("DOW", "DOW");
  filePromoGroups("LEAD_TIME", "LEAD_TIME");

  if (corridorData.length > 0 && !isDismissed("corridor")) {
    fileCard("CORRIDOR", {
      key: "corridor",
      type: CARD_TYPE.CORRIDOR,
      group: "CORRIDOR",
      payload: { corridorData, propertyId: data?.propertyId },
    });
  }

  if (
    ariData.length > 0 &&
    !isDismissed("ari") &&
    !supersededGroups.has("ARI")
  ) {
    fileCard("ARI", {
      key: "ari",
      type: CARD_TYPE.ARI,
      group: "ARI",
      payload: { ariData },
    });
  }

  // ── v2 (LLM) cards ────────────────────────────────────────────────────────
  (v2Data?.cards || []).forEach((card, i) => {
    const group = v2GroupOf(card);
    if (group === "ALIGNMENT" && alignmentSupersededByV1) return;
    const key = `llm-${card?.recommendationId ?? card?.rank ?? i}`;
    if (isDismissed(key)) return;
    fileCard(group, {
      key,
      type: CARD_TYPE.LLM,
      group,
      payload: { card },
    });
  });

  // Pin (EO-1893 deep link) BEFORE the BnBerry splice, exactly as the original
  // ActionCenter did: otherwise pinning a card that sorts after the BnBerry slot
  // would shift BnBerry off its intended slot 3. A null/absent pinnedKey is a no-op.
  const ordered = pinCardFirst(orderBySequence(byGroup), pinnedKey);

  // BnBerry card: recommendation slots in at position 3 (or the end if fewer
  // cards precede it); pending sits last of all. Count-affecting, so decided here.
  if (bnberry && !isDismissed("bnberry")) {
    const descriptor = {
      key: "bnberry",
      type: CARD_TYPE.BNBERRY,
      group: "BNBERRY",
      payload: { bnberry },
    };
    if (bnberry?.cardState === BNBERRY_CARD_STATE.PENDING) {
      ordered.push(descriptor);
    } else {
      const slot = Math.min(BNBERRY_DEFAULT_SLOT, ordered.length);
      ordered.splice(slot, 0, descriptor);
    }
  }

  return ordered;
};
